<template>
  <div class="manager-analytics-list">
    <template v-if="isMobileDevice">
      <div v-if="items.length">
        <ManagerAnalyticsCard
          v-for="(item, index) in items"
          :key="`${item.managerName}-${index}`"
          :item="item"
        />

        <ManagerAnalyticsCard
          v-if="totals && items.length"
          :item="totals"
          :title="$t('label.total')"
          is-total
        />
      </div>

      <SkeletonLoaderCard v-else :table-headers="skeletonHeaders" />
    </template>

    <OurTable
      v-else
      :headers="tableHeaders"
      :items="items"
      :can-navigate="false"
      :table-fixed="false"
      @sort="onSort"
    >
      <template #cell-managerName="{ value }">
        <span class="font-medium text-gray-900">{{ value }}</span>
      </template>

      <template #cell-received="{ value }">
        <span class="font-bold text-gray-900">{{ formatCount(value) }}</span>
      </template>

      <template #cell-rejected="{ value }">
        <span class="font-bold text-gray-900">{{ formatCount(value) }}</span>
      </template>

      <template #cell-inProgress="{ value }">
        <span class="font-bold text-gray-900">{{ formatCount(value) }}</span>
      </template>

      <template #cell-dealerCreated="{ value }">
        <ManagerAnalyticsMetricCell v-bind="formatCountMetricCell(value)" />
      </template>

      <template #cell-madeOrder="{ value }">
        <ManagerAnalyticsMetricCell v-bind="formatCountMetricCell(value)" />
      </template>

      <template #cell-ordersSum="{ value }">
        <ManagerAnalyticsMetricCell v-bind="formatOrdersSumCell(value)" />
      </template>

      <template #cell-shippedSum="{ value }">
        <ManagerAnalyticsMetricCell v-bind="formatSumMetricCell(value)" />
      </template>

      <template #cell-noOrders="{ value }">
        <ManagerAnalyticsMetricCell v-bind="formatCountMetricCell(value)" />
      </template>

      <template v-if="totals && items.length" #tfoot>
        <td class="analytics-total-cell !p-2">{{ $t("label.total") }}</td>
        <td class="analytics-total-cell">{{ formatCount(totals.received) }}</td>
        <td class="analytics-total-cell !p-2">{{ formatCount(totals.rejected) }}</td>
        <td class="analytics-total-cell !p-2">{{ formatCount(totals.inProgress) }}</td>
        <td class="analytics-total-cell !p-2">
          <ManagerAnalyticsMetricCell v-bind="formatCountMetricCell(totals.dealerCreated)" />
        </td>
        <td class="analytics-total-cell !p-2">
          <ManagerAnalyticsMetricCell v-bind="formatCountMetricCell(totals.madeOrder)" />
        </td>
        <td class="analytics-total-cell !p-2">
          <ManagerAnalyticsMetricCell v-bind="formatOrdersSumCell(totals.ordersSum)" />
        </td>
        <td class="analytics-total-cell !p-2">
          <ManagerAnalyticsMetricCell v-bind="formatSumMetricCell(totals.shippedSum)" />
        </td>
        <td class="analytics-total-cell !p-2">
          <ManagerAnalyticsMetricCell v-bind="formatCountMetricCell(totals.noOrders)" />
        </td>
      </template>
    </OurTable>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import SkeletonLoaderCard from "@/ui/loader/SkeletonLoaderCard.vue";
import ManagerAnalyticsCard from "./ManagerAnalyticsCard.vue";
import ManagerAnalyticsMetricCell from "./ManagerAnalyticsMetricCell.vue";
import {
  formatAnalyticsCount,
  formatCountMetricCell,
  formatOrdersSumCell,
  formatSumMetricCell,
} from "../config/analyticsCards.config";

const SORTABLE_COLUMNS = [
  "managerName",
  "received",
  "rejected",
  "inProgress",
  "dealerCreated",
  "madeOrder",
  "ordersSum",
  "shippedSum",
  "noOrders",
];

export default {
  name: "ManagerAnalyticsList",

  components: {
    SkeletonLoaderCard,
    ManagerAnalyticsCard,
    ManagerAnalyticsMetricCell,
  },

  props: {
    items: {
      type: Array,
      default: () => [],
    },

    totals: {
      type: Object,
      default: null,
    },

    orderBy: {
      type: Object,
      default: () => ({ column: "received", direction: "desc" }),
    },
  },

  computed: {
    ...mapGetters("breakpoint", ["isMobileDevice"]),

    skeletonHeaders() {
      return [
        { value: "name" },
        { value: "quantity" },
        { value: "price" },
        { value: "availability" },
      ];
    },

    tableHeaders() {
      return [
        {
          value: "managerName",
          text: this.$t("label.manager"),
          thClass: "w-[12rem] !p-2",
          tdClass: "max-w-[12rem] min-w-[12rem] !p-2",
          sortable: "managerName",
          sortDirection: this.getSortDirection("managerName"),
        },
        {
          value: "received",
          text: this.$t("label.received"),
          thClass: "w-[5rem] !p-2",
          tdClass: "max-w-[6rem] min-w-[6rem] !p-2",
          sortable: "received",
          sortDirection: this.getSortDirection("received"),
        },
        {
          value: "rejected",
          text: this.$t("label.rejected"),
          thClass: "w-[5rem] !p-2",
          tdClass: "max-w-[6rem] min-w-[6rem] !p-2",
          sortable: "rejected",
          sortDirection: this.getSortDirection("rejected"),
        },
        {
          value: "inProgress",
          text: this.$t("label.inProgress"),
          thClass: "w-[5rem] !p-2",
          tdClass: "max-w-[6rem] min-w-[6rem] !p-2",
          sortable: "inProgress",
          sortDirection: this.getSortDirection("inProgress"),
        },
        {
          value: "dealerCreated",
          text: this.$t("label.dealerCreated"),
          thClass: "w-[5rem] !p-2",
          tdClass: "max-w-[6rem] min-w-[6rem] !p-2",
          sortable: "dealerCreated",
          sortDirection: this.getSortDirection("dealerCreated"),
        },
        {
          value: "madeOrder",
          text: this.$t("label.madeOrder"),
          thClass: "w-[5rem] !p-2",
          tdClass: "max-w-[6rem] min-w-[6rem] !p-2",
          sortable: "madeOrder",
          sortDirection: this.getSortDirection("madeOrder"),
        },
        {
          value: "ordersSum",
          text: this.$t("label.ordersSum"),
          thClass: "w-[5rem] !p-2",
          tdClass: "max-w-[6rem] min-w-[6rem] !p-2",
          sortable: "ordersSum",
          sortDirection: this.getSortDirection("ordersSum"),
        },
        {
          value: "shippedSum",
          text: this.$t("label.shippedSum"),
          thClass: "w-[5rem] !p-2",
          tdClass: "max-w-[6rem] min-w-[6rem] !p-2",
          sortable: "shippedSum",
          sortDirection: this.getSortDirection("shippedSum"),
        },
        {
          value: "noOrders",
          text: this.$t("label.noOrders"),
          thClass: "w-[5rem] !p-2",
          tdClass: "max-w-[6rem] min-w-[6rem] !p-2",
          sortable: "noOrders",
          sortDirection: this.getSortDirection("noOrders"),
        },
      ];
    },
  },

  methods: {
    formatCount: formatAnalyticsCount,
    formatCountMetricCell,
    formatSumMetricCell,
    formatOrdersSumCell,

    getSortDirection(field) {
      if (this.orderBy.column !== field) {
        return "";
      }

      return this.orderBy.direction;
    },

    onSort(header) {
      if (!SORTABLE_COLUMNS.includes(header.sortable)) {
        return;
      }

      this.$emit("sort", header);
    },
  },
};
</script>

<style lang="postcss" scoped>
.manager-analytics-list:deep(.table-footer-row),
.manager-analytics-list:deep(.table-footer-fixed-row) {
  @apply bg-gray-50;
}

.manager-analytics-list:deep(.analytics-total-cell) {
  @apply bg-gray-50 font-bold text-gray-900;
}
</style>
